/**
 * DockingLayout — flexlayout-react wrapper for the project workspace.
 *
 * Provides side-by-side panel docking (DAG, Data Explorer, Plot Viewer, etc.)
 * using flexlayout-react directly (no FlexShell dependency).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Layout, Model, TabNode, IJsonModel } from "flexlayout-react";
import "flexlayout-react/style/dark.css";

import DAGPanel from "../panels/DAGPanel";
import DataExplorerPanel from "../panels/DataExplorerPanel";
import PlotViewerPanel from "../panels/PlotViewerPanel";
import ReproducibilityPanel from "../panels/ReproducibilityPanel";
import ArtifactDetailPanel from "../panels/ArtifactDetailPanel";
import Flow360ViewerPanel from "../panels/Flow360ViewerPanel";
import PlotBuilderPanel from "../panels/PlotBuilderPanel";
import SavedPlotsPanel from "../panels/SavedPlotsPanel";
import ForkPanel from "../../flow360/ForkPanel";
import { VersionHistory, DiffView, VersionHistoryPanel } from "../versioning";
import ConfigurationPanel from "../panels/ConfigurationPanel";
import ConversationsPanel from "../panels/ConversationsPanel";
import { createPlot } from "../../api/plots";
import type { PlotBuilderConfig } from "../plot-builder/types";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useReferenceQuantities } from "../../hooks/useReferenceQuantities";
import { extractWingArea } from "../../hooks/usePlotComparison";

import { defaultLayout } from "./panelConfig";

interface Props {
  projectId: string;
  onModelReady?: (model: Model) => void;
}

export function DockingLayout({ projectId, onModelReady }: Props) {
  const [model] = useState(() => Model.fromJson(defaultLayout as IJsonModel));
  const { currentBranch } = useProvenanceContext();
  const { data: refQuantities } = useReferenceQuantities(projectId, currentBranch);
  const notified = useRef(false);
  const [loadedPlotConfig, setLoadedPlotConfig] = useState<PlotBuilderConfig | undefined>();

  const handleLoadPlot = useCallback(
    (config: PlotBuilderConfig, _data: Record<string, unknown>[]) => {
      setLoadedPlotConfig(config);
      // Activate the Plot Builder tab so the user sees the loaded config
      try {
        model.doAction({ type: "FlexLayout_SelectTab", data: { tabNode: "plot-builder" } });
      } catch {
        // tab may not exist if user closed it
      }
    },
    [],
  );

  useEffect(() => {
    if (onModelReady && !notified.current) {
      notified.current = true;
      onModelReady(model);
    }
  }, [onModelReady]);

  const handleSavePlot = useCallback(
    async (config: PlotBuilderConfig, data: Record<string, unknown>[]) => {
      const nodeIds = [
        ...new Set(
          data
            .map((r) => r._node_id as string | undefined)
            .filter((id): id is string => !!id),
        ),
      ];
      const { value: wingArea, units: wingAreaUnits } = extractWingArea(refQuantities);
      const configWithSnapshot: Record<string, unknown> = {
        ...(config as unknown as Record<string, unknown>),
        ...(wingArea != null && { snapshotRefArea: wingArea, snapshotRefAreaUnits: wingAreaUnits ?? "m²" }),
      };
      await createPlot({
        project_id: projectId,
        title: config.title || "Untitled Plot",
        chart_type: config.chartType,
        config: configWithSnapshot,
        data_snapshot: data,
        source_node_ids: nodeIds,
        branch: currentBranch,
        scope: "branch",
      });
    },
    [projectId, currentBranch, refQuantities],
  );

  const factory = useCallback(
    (node: TabNode) => {
      const component = node.getComponent();
      switch (component) {
        case "dag":
          return <DAGPanel projectId={projectId} />;
        case "data-explorer":
          return <DataExplorerPanel projectId={projectId} />;
        case "plot-viewer":
          return <PlotViewerPanel />;
        case "plot-builder":
          return <PlotBuilderPanel projectId={projectId} onSave={handleSavePlot} initialConfig={loadedPlotConfig} />;
        case "saved-plots":
          return <SavedPlotsPanel projectId={projectId} onLoadPlot={handleLoadPlot} />;
        case "reproducibility":
          return <ReproducibilityPanel projectId={projectId} />;
        case "artifact-detail":
          return <ArtifactDetailPanel />;
        case "flow360-viewer":
          return <Flow360ViewerPanel projectId={projectId} />;
        case "fork-sweep":
          return <ForkPanel projectId={projectId} />;
        case "version-history":
          return <VersionHistory />;
        case "diff-view":
          return <DiffView />;
        case "history":
          return <VersionHistoryPanel projectId={projectId} />;
        case "configuration":
          return <ConfigurationPanel projectId={projectId} />;
        case "conversations":
          return <ConversationsPanel />;
        default:
          return <div>Unknown panel: {component}</div>;
      }
    },
    [projectId, handleSavePlot, handleLoadPlot, loadedPlotConfig],
  );

  return (
    <div style={{ flex: 1, position: "relative" }}>
      <Layout model={model} factory={factory} />
    </div>
  );
}
